/**
 * テーブルの横スクロール UI（NyTableScroll）とセルのツールチップ（NyTooltip）。
 *
 * **旧スタック（gulp + jQuery）と React の両方がこの 1 本を読む。**
 * もともと src/js/common/common.js の末尾に置いていたが、React 化した画面は
 * common.min.js を読まない（あちらは jQuery 前提のトップレベル文とサイドバー描画を
 * 含み、React の画面では動かない）ため、そのままでは 9〜11 列の表で横スクロールの
 * 到達手段とツールチップが丸ごと消えていた。
 * React 側へ書き直すと実装が二重化して必ずずれるので、**切り出して共有する**。
 *   - 旧スタック … 各 HTML が common.min.js の直後に table-ui.min.js を読む
 *   - React     … react/vite.config.ts の HEAD_TAGS が全エントリへ注入する
 *
 * 中身は移設前と同一（挙動を変えていない）。どちらのモジュールも
 * jQuery に依存せず、DOMContentLoaded と MutationObserver で自走する。
 *
 * IE11 は対象外（closest / MutationObserver を無条件に使う）。
 */

/* ===== テーブルセルのツールチップ =====
 *
 * 切り詰められた URL / タイトルのセルにホバーすると全文を出す。
 *
 * 自走式にしている理由: common.min.js とページ固有 JS の読み込み順はページに
 * よって前後する（article/list.js 系は common が後、query/priority-list.js 系は
 * common が先）。ページ固有 JS 側から呼ぶ設計にすると、順序に依存して未定義参照に
 * なりうる。行の描画を MutationObserver で検知して自分で走査すれば順序に依らない。
 *
 * IE11 は対象外（closest / MutationObserver を無条件に使う）。
 */
(function (global) {
  'use strict';

  var SHOW_DELAY = 300;
  var HIDE_DELAY = 100;
  var GAP = 6;
  var SCAN_DEBOUNCE = 120;

  /* 系統ごとにクラス名が違う。実測に基づく一覧（設計書 8.5.2b）。
     `td.-url` は c-common-list-table（rewrite/list.js）だけが使うハイフン始まりの
     別系統の命名。Phase C で URL 列を 2 行クランプした以上、全文への到達手段が
     無いと URL の末尾が読めなくなるためここに加えている。 */
  var URL_SEL = 'td.url, td.page-column, td.cell-url, td.-url';
  /* td.keyword はメインKW列。内部リンク 2 画面（group/links.js /
     manual_interlink/links.js・幅 135px + ellipsis）と KWグループパフォーマンス
     （group/performance.js）の 3 画面にある。日本語 KW は日常的に切り詰められるのに
     対象から漏れていた（2026-07-28 ユーザー報告）。KW はリンクではないので
     fullTextOf はセルの textContent にフォールバックする。 */
  var TITLE_SEL = 'td.title, td.title-column, td.cell-title, td.keyword';
  var MARK_ATTR = 'data-tooltip';
  var OWNED_ATTR = 'data-tooltip-owned-title';

  var TIP_CLASS = 'c-tooltip';

  var tip = null;
  /* 表示中（または表示待ち確定後）のアンカー。行の再描画で DOM から外れたときに
     チップを閉じるため、参照を保持しておく。 */
  var activeAnchor = null;
  var showTimer = null;
  var hideTimer = null;
  var scanTimer = null;

  /* hover 可否は init() で一度だけ確定させる。mark() は数千セルに対して毎スキャン
     走るため matchMedia を都度呼ばない。また init() のリスナ構成と mark() の判定が
     食い違うと、修正した二重表示が再発しうる。 */
  var hoverCapable = false;

  function detectHover() {
    return !!(global.matchMedia && global.matchMedia('(hover: hover)').matches);
  }

  /* 横（ellipsis）と縦（line-clamp）の両方で切り詰めを見る */
  function isTruncated(td) {
    if (td.scrollWidth > td.clientWidth + 1) return true;
    var inner = td.firstElementChild;
    if (inner && inner.scrollHeight > inner.clientHeight + 1) return true;
    return false;
  }

  /* URL は表示がパスだけのことが多いので、リンクの絶対 URL を全文として使う */
  function fullTextOf(td) {
    var a = td.querySelector('a[href]');
    if (a && a.href) return a.href;
    return (td.textContent || '').trim();
  }

  function mark(td) {
    var text = fullTextOf(td);
    if (text && isTruncated(td)) {
      td.setAttribute(MARK_ATTR, text);
      /* ネイティブ title はホバーできない環境のフォールバックとしてのみ付ける。
         ホバー環境で付けるとブラウザ標準のツールチップと .c-tooltip が二重に出る
         （既存の custom-tooltip 実装が抱えていた不具合と同じ）。
         また自分が付けたものだけ管理する（元から有るものは触らない）。 */
      if (!hoverCapable && !td.getAttribute('title')) {
        td.setAttribute('title', text);
        td.setAttribute(OWNED_ATTR, '1');
      }
    } else {
      td.removeAttribute(MARK_ATTR);
      if (td.getAttribute(OWNED_ATTR)) {
        td.removeAttribute('title');
        td.removeAttribute(OWNED_ATTR);
      }
    }
  }

  function refresh(root) {
    var scope = root || document;
    var cells = scope.querySelectorAll(URL_SEL + ', ' + TITLE_SEL);
    for (var i = 0; i < cells.length; i++) mark(cells[i]);
  }

  function scheduleScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(function () {
      /* レイアウト確定後でないと切り詰め判定ができない */
      global.requestAnimationFrame(function () { refresh(document); });
    }, SCAN_DEBOUNCE);
  }

  function removeTip() {
    if (tip && tip.parentNode) tip.parentNode.removeChild(tip);
    tip = null;
    activeAnchor = null;
  }

  function cancelTimers() {
    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
  }

  /* ===== MutationObserver の自己再発火対策 =====
   *
   * show() は .c-tooltip を document.body に append し、removeTip() が取り除く。
   * 監視は document.body の childList + subtree なので、ホバー往復ごとに最大 2 回、
   * 全 URL/タイトルセルの再走査が走っていた（2241 行の実測テーブルでは無駄が大きい）。
   * チップ自身の出入りだけの変化は無視する。
   *
   * 走査範囲は document 全体のままにしている。行生成は tbody.innerHTML の全置換だけ
   * でなくセル単位の差し替えもあり、record.target が td のときに scope
   * .querySelectorAll では target 自身が対象外になる（走査漏れ）。
   * 走査漏れは余分な走査より悪いので、範囲の絞り込みは行わず自己発火の除去だけを行う。
   */
  function isTipNode(node) {
    return !!(node && node.nodeType === 1 && node.classList &&
      node.classList.contains(TIP_CLASS));
  }

  function isSelfMutation(rec) {
    var i;
    for (i = 0; i < rec.addedNodes.length; i++) {
      if (!isTipNode(rec.addedNodes[i])) return false;
    }
    for (i = 0; i < rec.removedNodes.length; i++) {
      if (!isTipNode(rec.removedNodes[i])) return false;
    }
    return true;
  }

  function onMutations(records) {
    var needScan = false;
    for (var i = 0; i < records.length; i++) {
      if (!isSelfMutation(records[i])) { needScan = true; break; }
    }

    /* 表示中に行が差し替わるとチップが迷子になる（何も指していないチップが残る）。
       アンカーが DOM から外れていたら閉じる。 */
    if (activeAnchor && !activeAnchor.isConnected) {
      cancelTimers();
      removeTip();
    }

    if (needScan) scheduleScan();
  }

  function place(el, anchor) {
    var r = anchor.getBoundingClientRect();
    var tr = el.getBoundingClientRect();
    var left = r.left + global.pageXOffset;
    var top = r.top + global.pageYOffset - tr.height - GAP;

    var maxLeft = global.pageXOffset + document.documentElement.clientWidth - tr.width - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < global.pageXOffset + 8) left = global.pageXOffset + 8;

    /* 上に置けなければ下へ反転 */
    if (top < global.pageYOffset + 4) top = r.bottom + global.pageYOffset + GAP;

    el.style.left = left + 'px';
    el.style.top = top + 'px';
  }

  function show(anchor) {
    /* 待機中のタイマーを必ず畳んでから作り直す。focusin のように show() を直接
       呼ぶ経路があるため、ここで畳まないと後から発火したタイマーがチップを
       作り直したり即座に消したりする。 */
    cancelTimers();
    removeTip();
    /* 行生成 JS は tbody.innerHTML の全置換で再描画するため、表示待ちの 300ms 中に
       アンカーが DOM から外れうる。切り離された要素の矩形は全ゼロになり、画面左上に
       迷子のチップが出る。 */
    if (!anchor.isConnected) return;
    var text = anchor.getAttribute(MARK_ATTR);
    if (!text) return;

    var el = document.createElement('div');
    el.className = TIP_CLASS;
    el.textContent = text;
    document.body.appendChild(el);
    tip = el;
    activeAnchor = anchor;
    place(el, anchor);
    global.requestAnimationFrame(function () {
      if (tip === el) el.classList.add('-visible');
    });
  }

  function scheduleHide() {
    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      hideTimer = null;
      removeTip();
    }, HIDE_DELAY);
  }

  function anchorOf(node) {
    return node && node.closest ? node.closest('[' + MARK_ATTR + ']') : null;
  }

  function init() {
    /* 走査（切り詰め判定と属性付与）はホバー可否に関係なく行う。
       ここで早期 return すると、タッチ環境では title フォールバックすら
       付かなくなる。ホバー用のリスナだけを canHover() で出し分ける。 */
    hoverCapable = detectHover();

    if (!hoverCapable) {
      global.addEventListener('resize', scheduleScan);
      var moTouch = new MutationObserver(onMutations);
      moTouch.observe(document.body, { childList: true, subtree: true });
      scheduleScan();
      return;
    }

    document.addEventListener('mouseover', function (e) {
      var anchor = anchorOf(e.target);
      if (!anchor) return;
      /* 同じセル内の子要素間を移動しただけならタイマーを積み直さない */
      if (e.relatedTarget && anchor.contains(e.relatedTarget)) return;
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      if (showTimer) clearTimeout(showTimer);
      showTimer = setTimeout(function () {
        showTimer = null;
        show(anchor);
      }, SHOW_DELAY);
    });

    document.addEventListener('mouseout', function (e) {
      var anchor = anchorOf(e.target);
      if (!anchor) return;
      if (e.relatedTarget && anchor.contains(e.relatedTarget)) return;
      scheduleHide();
    });

    /* キーボード操作: td はフォーカスできないので、セル内のリンクに反応させる */
    document.addEventListener('focusin', function (e) {
      var anchor = anchorOf(e.target);
      if (anchor) show(anchor);
    });
    document.addEventListener('focusout', function (e) {
      if (anchorOf(e.target)) { cancelTimers(); removeTip(); }
    });

    /* スクロール・リサイズで位置がずれるので、表示中のものも待機中のものも止める */
    global.addEventListener('scroll', function () { cancelTimers(); removeTip(); }, true);
    global.addEventListener('resize', function () {
      cancelTimers();
      removeTip();
      scheduleScan();
    });

    /* 行は API 応答後に描画されるので、DOM の変化を見て再走査する。
       チップ自身の出入りは onMutations 側で除外している。 */
    var mo = new MutationObserver(onMutations);
    mo.observe(document.body, { childList: true, subtree: true });

    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.NyTooltip = { refresh: refresh };
})(window);

/* ===== B-2: 横スクロールへの到達性（設計書 §5.2 / §5.3）=====
 *
 * 2026-07-28: 上部同期スクロールバーは撤去した（ユーザー判断）。
 * macOS ではネイティブのつまみがオーバーレイでホバーまで出ず、空のグレー溝が
 * 視覚ノイズになるだけだった。要件（クリックだけで全列到達）は矢印 + 端フェードで
 * 満たせていることを実測済み（週次 7866px を 10 クリック）。
 *
 * ホイールマウスでは横スクロールの入力手段が Shift+ホイール / チルト /
 * バーのドラッグしか無い。加えて本体の横バーはスクロール容器の下端に付くため、
 * 行数が多い画面では画面外に出て存在に気づけない（2026-07-27 実測: 集客記事 週次は
 * 容器の下端が 1137px = 画面外 237px）。
 *
 * 要件は「Shift+ホイールを知らない利用者がクリック操作だけで全列に到達できること」。
 * そのために (a) 容器の直前に同期した横バー、(b) 端フェード + 押し続けで動く矢印を足す。
 *
 * **wheel には一切触らない。** 既存の Shift+ホイールを奪わないため、また縦ホイール →
 * 横スクロールの変換は設計書 §5.4 で不採用と決まっているため。
 *
 * 新しいファイルを作らずここに置いている理由: このプロジェクトの <script> は画面ごとに
 * 相対パスでハードコードされており共有パーシャルが無い。新規ファイルを作っても
 * 読み込まれず静かに死ぬ。common/common.min.js は 37 のテーブル画面すべてで読まれる。
 *
 * 自走式（MutationObserver で再走査）にしている理由も c-tooltip と同じ。
 * 行は API 応答後に描かれ、common.min.js とページ固有 JS の読み込み順は画面によって前後する。
 */
(function (global) {
  'use strict';

  var WRAP_CLASS = 'c-table-scroll';
  var ARROW_CLASS = 'c-table-scroll__arrow';
  /* 処理済みの容器に付ける印。再走査を冪等にする */
  var DONE_ATTR = 'data-table-scroll';

  var SCAN_DEBOUNCE = 120;
  /* 端判定と溢れ判定の許容。scrollWidth / clientWidth は小数を丸めた整数を返すため、
     0 で比較すると溢れていないのに 1px 溢れていると誤判定する。 */
  var TOLERANCE = 1;
  /* 1 クリックで動く量（見えている幅に対する割合）。1.0 にすると
     直前まで見えていた列が完全に消えて位置を見失う。 */
  var STEP_RATIO = 0.8;
  var STEP_MIN = 80;
  /* 押し続けと判定するまでの時間と、その後の刻み */
  var HOLD_DELAY = 400;
  var HOLD_INTERVAL = 60;
  var HOLD_STEP = 40;
  /* pointerdown から何 ms 以内の click をポインタ由来と見なすか。
     ブラウザが pointerup の直後に出す click は数 ms 以内に来る。長押しでも
     pointerdown からは 1 秒に届かないので余裕を見て 1000ms にする。 */
  var POINTER_CLICK_WINDOW = 1000;

  var records = [];
  var scanTimer = null;

  function isOverflowing(el) {
    return el.scrollWidth - el.clientWidth > TOLERANCE;
  }

  /* 容器の探索をクラス名で決め打ちにしない。系統によってスクロールするのが
     <table> 自身かラッパー div かに分かれるため（設計書 §5.1 の確定表）。
     クラス名で書くと <table> 側の 4 系統に効かない。 */
  function findContainers() {
    var all = document.querySelectorAll('div, table');
    var potential = [];
    var i, j, el, cs;

    /* 1 段目: 「横スクロール容器になり得る本表」を、**溢れの有無に関係なく**列挙する。
       溢れを先に見ると、内側の本表がその時点で溢れていないだけのときに
       外側ラッパーが唯一の候補として残ってしまう。c-store は本表（.table-main）が
       横を吸う一方で外側ラッパーもサマリー表のせいで溢れ得るので、
       この順序だと設計書 §5.1 が B-2 対象外としている外側に UI が付く（Codex 指摘）。 */
    for (i = 0; i < all.length; i++) {
      el = all[i];
      cs = global.getComputedStyle(el);
      if (cs.overflowX !== 'auto' && cs.overflowX !== 'scroll') continue;
      if (el.tagName !== 'TABLE' && !el.querySelector('table')) continue;
      /* 月別サマリー表は対象外（設計書 §5.1 の補助スクロール容器）。
         自前の横バーが画面内にあり（実測で高さ 87px・下端 473px）ドラッグで
         到達できるうえ、本表の上部バーと二重になる。c-event / c-store の
         両系統で `.table-summary` の 1 クラスに統一されているのでこれで判定する。

         **`thead` の有無で判定してはいけない。** c-reporting は `tr` を `table`
         直下に生成し thead を作らないが、§5.1 の確定表では B-2 の適用対象
         （Codex 指摘）。クラス名の決め打ちを避けたかったが、`thead` は本表と
         サマリー表を分ける指標になっていなかった。 */
      if (el.classList.contains('table-summary') || el.closest('.table-summary')) continue;
      /* モーダル内は対象外（設計書 §5.1: 上部バーを置く余地が無い） */
      if (el.closest('.ip-modal')) continue;
      potential.push(el);
    }

    /* 2 段目: 内側に別の候補を持つものを落とす。溢れの判定より前に行う。 */
    var innermost = [];
    for (i = 0; i < potential.length; i++) {
      var containsOther = false;
      for (j = 0; j < potential.length; j++) {
        if (j !== i && potential[i].contains(potential[j])) {
          containsOther = true;
          break;
        }
      }
      if (!containsOther) innermost.push(potential[i]);
    }

    /* 3 段目: 実際に横へ溢れているものだけに UI を付ける。 */
    var out = [];
    for (i = 0; i < innermost.length; i++) {
      if (isOverflowing(innermost[i])) out.push(innermost[i]);
    }
    return out;
  }

  function recordOf(container) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].container === container) return records[i];
    }
    return null;
  }

  function maxScrollLeft(el) {
    return Math.max(0, el.scrollWidth - el.clientWidth);
  }

  function updateEdges(rec) {
    var c = rec.container;
    var max = maxScrollLeft(c);
    /* クラスを変える前にフォーカス中の矢印を覚えておく。リサイズやデータ更新で
       矢印が消える経路は click を通らないので、ここでも救う必要がある（Codex 指摘）。 */
    var focused = null;
    if (document.activeElement === rec.arrowLeft) focused = rec.arrowLeft;
    else if (document.activeElement === rec.arrowRight) focused = rec.arrowRight;

    rec.wrap.classList.toggle('-at-start', c.scrollLeft <= TOLERANCE);
    rec.wrap.classList.toggle('-at-end', c.scrollLeft >= max - TOLERANCE);

    if (focused && global.getComputedStyle(focused).display === 'none') {
      moveFocusOff(rec, focused);
    }
  }

  /* 消えた矢印から逃げ先へフォーカスを移す。反対側の矢印が生きていればそこへ、
     両方消えていれば（= 溢れが無くなった）容器自身へ。容器は tabindex を持たないので
     一時的に付け、blur で外す（unwrap と同じやり方。Tab の停止位置を増やさない）。 */
  function moveFocusOff(rec, from) {
    var other = from === rec.arrowLeft ? rec.arrowRight : rec.arrowLeft;
    if (other && global.getComputedStyle(other).display !== 'none') {
      other.focus();
      return;
    }
    var c = rec.container;
    if (!c.hasAttribute('tabindex')) {
      c.setAttribute('tabindex', '-1');
      c.addEventListener('blur', function onBlur() {
        c.removeAttribute('tabindex');
        c.removeEventListener('blur', onBlur);
      });
    }
    c.focus();
  }

  function prefersReducedMotion() {
    return !!(global.matchMedia &&
      global.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* 1 クリックぶんの移動。dir は -1（左）/ +1（右） */
  function step(rec, dir) {
    var c = rec.container;
    var amount = Math.max(STEP_MIN, Math.round(c.clientWidth * STEP_RATIO));
    if (c.scrollBy) {
      c.scrollBy({ left: dir * amount, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } else {
      c.scrollLeft += dir * amount;
    }
    updateEdges(rec);
  }

  function makeArrow(rec, dir) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = ARROW_CLASS + ' ' + (dir < 0 ? '-left' : '-right');
    btn.setAttribute('aria-label', dir < 0 ? '左へスクロール' : '右へスクロール');
    /* 画像を足さずに文字で置く（‹ / ›） */
    btn.textContent = dir < 0 ? '‹' : '›';

    var holdDelay = null;
    var holdTimer = null;
    /* 直近の pointerdown の時刻。ポインタでは pointerdown で既に動かしているので、
       後から飛んでくる click で二重に動かさない。キーボード（Enter / Space）は
       pointer イベントを出さないので click 側で拾う必要がある。 */
    var lastPointerAt = 0;

    function stopHold() {
      if (holdDelay) { clearTimeout(holdDelay); holdDelay = null; }
      if (holdTimer) { clearInterval(holdTimer); holdTimer = null; }
    }

    rec.stopHolds.push(stopHold);

    btn.addEventListener('pointerdown', function () {
      lastPointerAt = Date.now();
      stopHold();
      step(rec, dir);
      holdDelay = setTimeout(function () {
        holdDelay = null;
        /* 押し続けでは 1 クリックぶんを繰り返すと飛びすぎるので小刻みに動かす。
           smooth を使うとアニメーションが積み上がって操作から遅れるので使わない。 */
        holdTimer = setInterval(function () {
          var before = rec.container.scrollLeft;
          rec.container.scrollLeft += dir * HOLD_STEP;
          updateEdges(rec);
          /* 端に着いて動かなくなったら止める。押し続けたまま矢印が display: none に
             なると pointerup が届かず回り続ける（Codex 指摘）。 */
          if (rec.container.scrollLeft === before) stopHold();
        }, HOLD_INTERVAL);
      }, HOLD_DELAY);
    });
    btn.addEventListener('pointerup', stopHold);
    btn.addEventListener('pointerleave', stopHold);
    btn.addEventListener('pointercancel', stopHold);

    /* **`pointerleave` でフラグを降ろしてはいけない。** ホバーを持たない端末
       （タッチ）の標準的な順序は `pointerdown → pointerup → pointerleave → click` で、
       leave で降ろすと直後の click がポインタ由来と判定されず二重に動く
       （実測で scrollBy が 2 回呼ばれることを確認）。

       一方で `pointercancel` や要素外での `pointerup` では click が飛んでこないため、
       真偽フラグを持ち続けると次のキーボード操作が 1 回無視される。
       そこで**フラグではなく時刻**で持つ。pointerdown からの経過時間が短ければ
       その click はポインタ由来と見なす。ポインタ操作の後に人がキーボードへ
       持ち替えるには十分な間隔が空く。 */
    btn.addEventListener('click', function () {
      if (lastPointerAt && Date.now() - lastPointerAt < POINTER_CLICK_WINDOW) {
        lastPointerAt = 0;
        return;
      }
      step(rec, dir);
      /* 端に達して矢印が消えるときのフォーカス移譲は updateEdges が拾う
         （scroll イベントごとに「フォーカス中の矢印が消えたか」を見る）。
         かつて存在した handOffFocusIfHidden はバー撤去時に削除済み。 */
    });

    return btn;
  }

  function wrap(container) {
    var parent = container.parentNode;
    if (!parent) return;

    var rec = {
      container: container,
      wrap: null,
      ro: null,
      onScroll: null,
      arrowLeft: null,
      arrowRight: null,
      /* 押し続けのタイマーを止める関数。矢印ごとに登録し unwrap で全部呼ぶ。
         ボタンを DOM から外すと pointerup が届かずタイマーが残るため（Codex 指摘）。 */
      stopHolds: []
    };

    var w = document.createElement('div');
    w.className = WRAP_CLASS;
    rec.wrap = w;

    /* DOM を移し替えるとスクロール位置が 0 に戻るので退避して戻す。
       再走査は行の描画後にも走るため、ここで戻さないと利用者の位置が飛ぶ。 */
    var left = container.scrollLeft;
    var top = container.scrollTop;

    /* 容器にフォーカスがある状態で appendChild すると外れる（unwrap と同じ経路）。
       unwrap 直後に再び溢れて wrap されるとここを通るので、移動後に戻す。 */
    var hadFocus = document.activeElement === container;

    parent.insertBefore(w, container);
    w.appendChild(container);
    rec.arrowLeft = makeArrow(rec, -1);
    rec.arrowRight = makeArrow(rec, 1);
    w.appendChild(rec.arrowLeft);
    w.appendChild(rec.arrowRight);

    container.scrollLeft = left;
    container.scrollTop = top;
    container.setAttribute(DONE_ATTR, '1');
    if (hadFocus) container.focus();

    rec.onScroll = function () { updateEdges(rec); };
    container.addEventListener('scroll', rec.onScroll);

    /* 列幅は行の描画やウィンドウ幅で変わる。容器のサイズ変化で端の状態を測り直す。 */
    if (global.ResizeObserver) {
      rec.ro = new global.ResizeObserver(function () {
        updateEdges(rec);
      });
      rec.ro.observe(container);
    }

    updateEdges(rec);
    records.push(rec);
  }

  /* 横に溢れなくなった容器は元の位置に戻す。使えないバーと矢印が残ると邪魔になる。 */
  function unwrap(rec) {
    var w = rec.wrap;
    var parent = w.parentNode;
    /* 先にタイマーを止める。ボタンが消えると pointerup が届かず
       setInterval が回り続ける（Codex 指摘）。 */
    for (var k = 0; k < rec.stopHolds.length; k++) rec.stopHolds[k]();
    rec.stopHolds.length = 0;

    /* ラッパーごと消すので、この中にフォーカスがあると body に落ちる。
       updateEdges 経由の移譲では救えない経路（refresh が直接 unwrap する）なので
       ここでも逃がす（Codex 指摘）。逃げ先は消えない容器自身。
       **フォーカスを当てるのは DOM の移し替えより後**。先に当てても
       insertBefore で容器を動かした時点で外れる（実測で踏んだ）。 */
    var hadFocusInside = w.contains(document.activeElement);
    rec.container.removeEventListener('scroll', rec.onScroll);
    if (rec.ro) rec.ro.disconnect();
    if (parent) {
      var left = rec.container.scrollLeft;
      var top = rec.container.scrollTop;
      parent.insertBefore(rec.container, w);
      rec.container.scrollLeft = left;
      rec.container.scrollTop = top;
      parent.removeChild(w);

      if (hadFocusInside) {
        /* 容器は tabindex を持たないので一時的に付けてフォーカスし、
           blur したら外す（Tab の停止位置を増やさない）。 */
        if (!rec.container.hasAttribute('tabindex')) {
          rec.container.setAttribute('tabindex', '-1');
          rec.container.addEventListener('blur', function onBlur() {
            rec.container.removeAttribute('tabindex');
            rec.container.removeEventListener('blur', onBlur);
          });
        }
        rec.container.focus();
      }
    }
    rec.container.removeAttribute(DONE_ATTR);
    var i = records.indexOf(rec);
    if (i >= 0) records.splice(i, 1);
  }

  function refresh() {
    var wanted = findContainers();
    var i;

    for (i = records.length - 1; i >= 0; i--) {
      var rec = records[i];
      if (!rec.container.isConnected || wanted.indexOf(rec.container) < 0) {
        unwrap(rec);
      } else {
        updateEdges(rec);
      }
    }

    for (i = 0; i < wanted.length; i++) {
      if (!recordOf(wanted[i])) wrap(wanted[i]);
    }
  }

  function scheduleScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(function () {
      scanTimer = null;
      /* レイアウト確定後でないと scrollWidth / clientWidth が読めない */
      global.requestAnimationFrame(function () { refresh(); });
    }, SCAN_DEBOUNCE);
  }

  function isOwnNode(node) {
    if (!node || node.nodeType !== 1 || !node.classList) return false;
    return node.classList.contains(WRAP_CLASS) ||
      node.classList.contains(ARROW_CLASS) ||
      /* 同居しているツールチップ（common.js 前半の c-tooltip）のチップも無視する。
         チップはホバーのたびに body へ出し入れされるので、無視しないと
         ホバー往復ごとに容器の再走査が走る（tooltip 側は同じ問題を自分の
         observer で対策済み。こちらだけ漏れていた）。チップの中に table は
         入らないので、無視しても走査漏れにはならない。 */
      node.classList.contains('c-tooltip');
  }

  /* 自分が作った要素の出入りだけの変化は無視する。
     ただし wrap() は容器自身も付け替えるので、その 1 回は自己発火する。
     再走査は冪等（DONE_ATTR で処理済みを弾く）なので 1 回余分に走って収束する。 */
  function isSelfMutation(rec) {
    var i;
    for (i = 0; i < rec.addedNodes.length; i++) {
      if (!isOwnNode(rec.addedNodes[i])) return false;
    }
    for (i = 0; i < rec.removedNodes.length; i++) {
      if (!isOwnNode(rec.removedNodes[i])) return false;
    }
    return true;
  }

  function onMutations(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (!isSelfMutation(mutations[i])) { scheduleScan(); return; }
    }
  }

  function init() {
    /* 幅が変わると溢れの有無と列幅が変わるので測り直す */
    global.addEventListener('resize', scheduleScan);
    var mo = new MutationObserver(onMutations);
    mo.observe(document.body, { childList: true, subtree: true });
    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.NyTableScroll = { refresh: refresh };
})(window);